import { z } from 'zod';

import {
	preprocessEmptyStringAsNull,
	preprocessEmptyStringAsUndefined,
} from '@dealstack/shared/lib/common/common.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	DayCountConvention,
	PeriodDateType,
	PeriodUnitType,
	SecurityExchangeRightsPeriodBasis,
	SecurityExchangeTransactionType,
} from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const SecurityExchangeRightsShape = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>
) =>
	z
		.object({
			transactionType: z.nativeEnum(SecurityExchangeTransactionType),
			exchangedForSecurityId: z.preprocess(preprocessEmptyStringAsNull, z.string()),
			rate: z.preprocess(preprocessEmptyStringAsNull, z.number().int().positive()),
			periodBasis: z.nativeEnum(SecurityExchangeRightsPeriodBasis),
			periodBasisDate: z.preprocess(
				preprocessEmptyStringAsNull,
				z.string().datetime().optional().nullable()
			),
			periodBasisIntervalStartDuration: z.preprocess(
				preprocessEmptyStringAsNull,
				z
					.union([z.string(), z.number().int().positive().max(365)])
					.nullable()
					.optional()
			),
			periodBasisIntervalStartPeriod: z.nativeEnum(PeriodDateType).optional().nullable(),
			periodBasisIntervalEndDuration: z.preprocess(
				preprocessEmptyStringAsNull,
				z
					.union([z.string(), z.number().int().positive().max(365)])
					.nullable()
					.optional()
			),
			periodBasisIntervalEndPeriod: z.nativeEnum(PeriodDateType).optional().nullable(),
			price: z.preprocess(preprocessEmptyStringAsNull, z.number().nonnegative().optional()),
			additionalPriceRate: z.preprocess(
				preprocessEmptyStringAsUndefined,
				z.number().nonnegative().optional()
			),
			additionalPriceAccrualPeriod: z.nativeEnum(PeriodUnitType).optional(),
			additionalPriceDayCountConvention: z.nativeEnum(DayCountConvention).optional(),
			additionalPriceDeemedStartDate: z.preprocess(
				preprocessEmptyStringAsNull,
				z.string().datetime().optional().nullable()
			),
			additionalPriceCompoundingPeriod: z.nativeEnum(PeriodUnitType).optional().nullable(),
			additionalPriceFirstInterestDate: z.preprocess(
				preprocessEmptyStringAsNull,
				z.string().datetime().optional().nullable()
			),
		})
		.superRefine((data, ctx) => {
			const periodBasisFixedDate =
				data.periodBasis === SecurityExchangeRightsPeriodBasis.FIXED_DATE;
			const periodBasisRelativeToIssueDate =
				data.periodBasis === SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE;

			if (!periodBasisFixedDate && !periodBasisRelativeToIssueDate) {
				return;
			}

			const required = tCapManage('securities.validation.required');
			const addRequired = (path: string) =>
				ctx.addIssue({ code: z.ZodIssueCode.custom, message: required, path: [path] });

			const startDurationValid = typeof data.periodBasisIntervalStartDuration === 'number';
			const startPeriodValid = !!data.periodBasisIntervalStartPeriod;
			const endDurationValid = typeof data.periodBasisIntervalEndDuration === 'number';
			const endPeriodValid = !!data.periodBasisIntervalEndPeriod;

			if (periodBasisFixedDate && !data.periodBasisDate) {
				addRequired('periodBasisDate');
			}

			if (periodBasisFixedDate || periodBasisRelativeToIssueDate) {
				if (!endDurationValid) {
					addRequired('periodBasisIntervalEndDuration');
				}

				if (!endPeriodValid) {
					addRequired('periodBasisIntervalEndPeriod');
				}
			}

			if (periodBasisRelativeToIssueDate) {
				if (!startDurationValid) {
					addRequired('periodBasisIntervalStartDuration');
				}

				if (!startPeriodValid) {
					addRequired('periodBasisIntervalStartPeriod');
				}
			}
		})
		.refine(
			(data) => {
				const firstInterestDate = data.additionalPriceFirstInterestDate
					? new Date(data.additionalPriceFirstInterestDate)
					: null;
				const deemedStartDate = data.additionalPriceDeemedStartDate
					? new Date(data.additionalPriceDeemedStartDate)
					: null;

				if (!deemedStartDate || !firstInterestDate) {
					return true;
				}

				return firstInterestDate >= deemedStartDate;
			},
			{
				message: 'First interest date needs to be after deemed start date',
				path: ['additionalPriceFirstInterestDate'],
			}
		);

export type SecurityExchangeRightsFormDto = z.infer<ReturnType<typeof SecurityExchangeRightsShape>>;
